import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert } from '@mui/material';
import axiosInstant from '../../../api';
import { useNetworkScope } from '../../../hooks/useNetworkScope';
import { getBrandProducts } from '../../../api/endpoints';
import { ProductListingView } from './productListView';
import { AddProductFromUrl } from './productListView/AddProductFromUrl';
import { SelectedProduct } from '../products/productDetailV2';
import { WhiteBackground } from '../../../components/WhiteBackground';

const LIMIT_VALUE = 20;

// Funnel landing (TDC-1573): registration queues the audited PDP as the
// brand's first product; ?welcome=1 keeps refetching until it shows up.
const WELCOME_POLL_INTERVAL_MS = 5000;
const WELCOME_POLL_MAX_MS = 3 * 60 * 1000;

export function ProductV2Listing() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<any[]>([]);
  const [productsCount, setProductsCount] = useState(0);
  const [limit, setLimit] = useState(LIMIT_VALUE);
  const [isLoading, setIsLoading] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<null | number>(
    null
  );
  // Inside the brand-portal workspace this is the opened brand; outside,
  // the logged-in user's own brand. basePath prefixes in-catalog links.
  const { brandId, basePath } = useNetworkScope();

  // `refresh` re-reads the rows already on screen (the background polls):
  // no spinner, and every loaded page, so a "Load more" list keeps its length.
  const fetchProducts = async (shouldAppend = false, refresh = false) => {
    if (!refresh) setIsLoading(true);
    const searchText = searchParams.get("search");
    const page = shouldAppend ? Math.floor(products.length / LIMIT_VALUE) + 1 : 1;
    let fetchLimit = limit;
    if (shouldAppend) fetchLimit = LIMIT_VALUE;
    else if (refresh) fetchLimit = Math.max(limit, products.length);
    const response = await axiosInstant.get(
      getBrandProducts(
        brandId,
        fetchLimit,
        page,
        searchText || "",
        searchParams.get("rmn_category_id") || "",
        searchParams.get("rmn_brand_id") || "",
        searchParams.get("product_category_id") || ""
      )
    );
    const { data } = response;
    if (shouldAppend) {
      if (data.rows.length > 0) {
        setProducts((prev) => [...prev, ...data.rows]);
      }
    } else {
      // Always replace — a filter/search with zero matches must clear the
      // previous rows rather than leave them on screen.
      setProducts(data.rows);
    }
    setProductsCount(data.count);
    setIsLoading(false);
  };
  // The polls below outlive the render that started them; through this ref
  // they always run the current fetch (current search, filters and rows).
  const fetchProductsRef = useRef(fetchProducts);
  fetchProductsRef.current = fetchProducts;
  const pollProducts = () => {
    fetchProductsRef.current(false, true).catch(() => {
      // a failed background refresh just waits for the next tick
    });
  };

  useEffect(() => {
    const productId = searchParams.get("productId");
    setSelectedProductId(productId ? parseInt(productId, 10) : null);
    fetchProducts();
  }, [searchParams]);

  const welcome = !!searchParams.get("welcome");
  const clearWelcome = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("welcome");
      return next;
    });
  };
  useEffect(() => {
    if (!welcome) return undefined;
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt > WELCOME_POLL_MAX_MS) {
        clearWelcome();
        return;
      }
      pollProducts();
    }, WELCOME_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [welcome]);
  useEffect(() => {
    if (welcome && !isLoading && productsCount > 0) clearWelcome();
  }, [welcome, isLoading, productsCount]);

  // Rows carry pdp_report_running / cv_running; refetch while any is
  // analyzing so the run icons re-enable and scores fill in on their own.
  const anyIntelRunning = products.some((p) => p.pdp_report_running || p.cv_running || p.aip_running);
  useEffect(() => {
    if (!anyIntelRunning) return undefined;
    const timer = setInterval(pollProducts, 8000);
    return () => clearInterval(timer);
  }, [anyIntelRunning]);

  const resetSelectedProduct = () => {
    // Drop only productId — active search/filter params must survive
    // closing the detail view.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("productId");
      return next;
    });
    setSelectedProductId(null);
  };

  return (
    <>
      {selectedProductId ? (
        // The detail view keeps the white body; the listing shares the
        // Catalog pages' default background.
        <WhiteBackground>
          <SelectedProduct
            brandId={brandId}
            productId={selectedProductId}
            onClose={resetSelectedProduct}
          />
        </WhiteBackground>
      ) : (
        <>
          {welcome && (
            <Alert severity="info" sx={{ mb: 2 }}>
              We&apos;re importing and scoring your audited product — this takes about a minute.
            </Alert>
          )}
          <ProductListingView
            isLoading={isLoading}
            count={productsCount}
            products={products}
            onClickNew={() => setShowProductModal(true)}
            onClickImport={() => navigate(`${basePath}/products/discovered`)}
            onUpdate={fetchProducts}
            onLoadMore={() => fetchProducts(true)}
          />
        </>
      )}

      {/* URL-first add (TDC-1499) — the manual form survives on the row
          menu's Edit action. */}
      <AddProductFromUrl
        open={showProductModal}
        onClose={() => setShowProductModal(false)}
        onImported={() => fetchProducts()}
      />
    </>
  );
}
